import {
  BodyMedium,
  SubtleButton,
  Checkbox,
  HeadingSmall,
  SelectWrapper,
  selectMenuStyles,
  theme,
  ToggleButton,
  DeprecatedCheckIcon,
  LineIcon,
} from '@arnold/common';
import styled from '@emotion/styled';
import { addMonths, startOfMonth } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SurveyGroupsQuery, SurveysQuery } from '../../../generated/hooks';
import { EmployeeRecordWithAdditionalData } from '../../../screens/UpdateTeam/OrgImportUtils';
import DateInput from '../../Common/DateInput';
import { TableContainer } from './styled';

export interface ScheduledProcessSurvey {
  employee: EmployeeRecordWithAdditionalData;
  surveyGroupId: string;
  addedOn: Date;
}

export interface ScheduledOneTimeSurvey {
  employee: EmployeeRecordWithAdditionalData;
  surveyId: string;
}

interface IProps {
  processSurveys: NonNullable<SurveyGroupsQuery['organization']>['surveyGroups'];
  employees: EmployeeRecordWithAdditionalData[];
  scheduledProcessSurveys: ScheduledProcessSurvey[];
  setScheduledProcessSurveys: (data: ScheduledProcessSurvey[]) => void;
  setNextButtonIsDisabled: (isDisabled: boolean) => void;
  heading: string;
  processSurveyColumnHeader: string;
}
const BulkEditContainer = styled.div`
  background-color: ${theme.colors.backgroundBasic.default};
  width: max-content;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: ${theme.spacing.h} 180px 210px 190px;
  align-items: baseline;
  padding: ${theme.spacing.e} 0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeader = styled(TableRow)`
  color: ${theme.colors.text.secondary};
  font-size: 13px;
  border: none;
`;

const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
`;

const SpaceBetweenDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ToggleButtonContainer = styled.div`
  margin-top: ${theme.spacing.d};
  margin-left: ${theme.spacing.f};
`;

export const NO_ONBOARDING_VALUE = 'no_onboarding';

export function AddToOnboardingOrProcessSurveyModal({
  processSurveys,
  employees,
  scheduledProcessSurveys,
  setScheduledProcessSurveys,
  setNextButtonIsDisabled,
  heading,
  processSurveyColumnHeader,
}: IProps) {
  const [t] = useTranslation('organizationImportCard');
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeRecordWithAdditionalData[]>([]);
  const defaultSurveyGroupId = processSurveys.length === 1 ? processSurveys[0].id : undefined;
  const [isBulkEdit, setIsBulkEdit] = useState(false);

  // State for bulk operations
  const [bulkDate, setBulkDate] = useState<Date | undefined>(undefined);
  const [bulkSurveyGroupId, setBulkSurveyGroupId] = useState<string | undefined>(undefined);
  const [showValidationError, setShowValidationError] = useState<boolean>(false);
  // Initialize with default dates on mount
  useEffect(() => {
    // Initialize with existing scheduled process surveys or empty array
    if (scheduledProcessSurveys.length === 0) {
      setSelectedEmployees([]);
    } else {
      const selectedEmployeesFromProps = scheduledProcessSurveys.map((item) => item.employee);
      setSelectedEmployees(selectedEmployeesFromProps);
    }
    // Only run on initial mount, not when scheduledProcessSurveys changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hide validation error when employees are selected
  useEffect(() => {
    if (selectedEmployees.length > 0) {
      setShowValidationError(false);
    }
  }, [selectedEmployees]);

  // Validate selections and update disabled state
  useEffect(() => {
    const isNextButtonDisabled =
      selectedEmployees.length > 0 && !validateSelections(selectedEmployees, scheduledProcessSurveys);

    setNextButtonIsDisabled(isNextButtonDisabled);
  }, [selectedEmployees, scheduledProcessSurveys, setNextButtonIsDisabled]);

  // Validate that all selected employees have both surveyGroupId and addedOn
  const validateSelections = (
    selected: EmployeeRecordWithAdditionalData[],
    scheduled: ScheduledProcessSurvey[],
  ): boolean => {
    if (selected.length === 0) return true;

    return selected.every((employee) => {
      const scheduledItem = scheduled.find((item) => item.employee === employee);
      return scheduledItem && scheduledItem.surveyGroupId && scheduledItem.addedOn;
    });
  };

  const handleEmployeeSelection = (employee: EmployeeRecordWithAdditionalData) => {
    const isSelected = selectedEmployees.some((e) => e === employee);

    if (isSelected) {
      // Remove employee from selection
      const newSelectedEmployees = selectedEmployees.filter((e) => e !== employee);
      setSelectedEmployees(newSelectedEmployees);

      // Note: We're not removing the employee from scheduledProcessSurveys
      // This preserves their settings if they're selected again
    } else {
      // Add employee to selection
      const newSelectedEmployees = [...selectedEmployees, employee];
      setSelectedEmployees(newSelectedEmployees);
    }
  };

  const handleSelectAll = (select: boolean) => {
    if (select) {
      setSelectedEmployees([...employees]);
    } else {
      setSelectedEmployees([]);
      // Don't clear scheduledProcessSurveys when deselecting all
      // This preserves the settings if the user selects all again
    }
  };

  const handleDateChange = (employee: EmployeeRecordWithAdditionalData, date: Date) => {
    const newScheduledProcessSurveys = [...scheduledProcessSurveys];
    const index = newScheduledProcessSurveys.findIndex((item) => item.employee === employee);

    if (index >= 0) {
      newScheduledProcessSurveys[index] = {
        ...newScheduledProcessSurveys[index],
        addedOn: date,
      };
    } else {
      // Find settings from another employee to use as a template
      const templateSettings = scheduledProcessSurveys.find((item) =>
        selectedEmployees.some((e) => e === item.employee && e !== employee),
      );

      newScheduledProcessSurveys.push({
        employee,
        // Use template surveyGroupId if available, otherwise use default
        surveyGroupId: templateSettings?.surveyGroupId || defaultSurveyGroupId || '',
        addedOn: date,
      });
    }

    setScheduledProcessSurveys(newScheduledProcessSurveys);
  };

  const handleSurveyChange = (employee: EmployeeRecordWithAdditionalData, surveyGroupId: string) => {
    const newScheduledProcessSurveys = [...scheduledProcessSurveys];
    const index = newScheduledProcessSurveys.findIndex((item) => item.employee === employee);

    if (index >= 0) {
      newScheduledProcessSurveys[index] = {
        ...newScheduledProcessSurveys[index],
        surveyGroupId,
      };
    } else {
      // Find settings from another employee to use as a template
      const templateSettings = scheduledProcessSurveys.find((item) =>
        selectedEmployees.some((e) => e === item.employee && e !== employee),
      );

      const today = new Date();
      const startOfCurrentMonth = startOfMonth(today);
      const startOfNextMonth = startOfMonth(addMonths(today, 1));
      const defaultAddedOn = today.getDate() === 1 ? startOfCurrentMonth : startOfNextMonth;

      newScheduledProcessSurveys.push({
        employee,
        surveyGroupId,
        // Use template addedOn if available, otherwise use default
        addedOn: templateSettings?.addedOn || defaultAddedOn,
      });
    }

    setScheduledProcessSurveys(newScheduledProcessSurveys);
  };

  const getScheduledItemForEmployee = (employee: EmployeeRecordWithAdditionalData) => {
    return scheduledProcessSurveys.find((item) => item.employee === employee);
  };

  const isAllSelected = employees.length > 0 && selectedEmployees.length === employees.length;
  const isSomeSelected = selectedEmployees.length > 0 && selectedEmployees.length < employees.length;

  const surveyOptions = [
    { value: NO_ONBOARDING_VALUE, label: t('no_onboarding') }, // dummy survey
    ...processSurveys.map((onb) => ({
      value: onb.id,
      label: onb.name || onb.topicGroup.name,
    })),
  ];

  const selectedBulkSurveyOption = bulkSurveyGroupId
    ? surveyOptions.find((option) => option.value === bulkSurveyGroupId)
    : undefined;

  return (
    <>
      <HeadingSmall className="mb-4">{heading}</HeadingSmall>
      <BodyMedium color={theme.colors.text.secondary}>
        <SpaceBetweenDiv>
          {`${t('addedEmployees')}: ${employees.length}`}
          <ToggleContainer>
            {`${t('editBulk')}`}
            <ToggleButtonContainer>
              <ToggleButton
                value={isBulkEdit}
                onChange={() => {
                  setIsBulkEdit(!isBulkEdit);
                }}
              />
            </ToggleButtonContainer>
          </ToggleContainer>
        </SpaceBetweenDiv>
      </BodyMedium>
      <TableContainer>
        {isBulkEdit && (
          <BulkEditContainer>
            <TableRow>
              <CheckboxCell></CheckboxCell>
              <div>
                <span>
                  {' '}
                  {t('selectedEmployees')} ({selectedEmployees.length})
                </span>
                {showValidationError && (
                  <ValidationError data-cy="employees-validation-error">
                    {t('selectAtLeastOneEmployee')}
                  </ValidationError>
                )}
              </div>
              <DateInputWrapper>
                <DateInput
                  onDateChange={(value) => {
                    setBulkDate(value);
                  }}
                  inputName={`bulk-addedOn`}
                  date={bulkDate ? bulkDate.toISOString() : undefined}
                  data-cy={'bulk-add-to-onboarding-input'}
                  autoPositioning={true}
                />
              </DateInputWrapper>
              <div>
                <SelectWrapper
                  options={surveyOptions}
                  placeholder={t('select')}
                  isSearchable={false}
                  onChange={(option: { value: string; label: string }) => {
                    setBulkSurveyGroupId(option.value);
                  }}
                  menuPlacement="top"
                  value={selectedBulkSurveyOption}
                  styles={{
                    menu: (provided: any) => ({
                      ...provided,
                      ...selectMenuStyles,
                      top: 'auto',
                      bottom: '100%',
                      marginBottom: `${theme.spacing.d}`,
                      backgroundColor: `${theme.background.primary}`,
                    }),
                    container: (provided: any) => ({
                      ...provided,
                    }),
                    control: (provided: any, state: any) => ({
                      ...provided,
                      paddingLeft: theme.spacing.f,
                      borderColor: state.isFocused
                        ? `${theme.colors.borderMain.active} !important`
                        : theme.colors.borderMain.default,
                      boxShadow: 'none',
                      width: '174px',
                    }),
                  }}
                  isDisabled={false}
                />
                <div style={{ marginTop: `${theme.spacing.d}`, display: 'flex', justifyContent: 'flex-end' }}>
                  <SubtleButton
                    style={{
                      marginTop: `${theme.spacing.d}`,
                      marginRight: `${theme.spacing.f}`,
                      marginBottom: `${theme.spacing.c}`,
                    }}
                    onClick={() => {
                      if (selectedEmployees.length === 0) {
                        // Show validation error if no employees are selected
                        setShowValidationError(true);
                        return;
                      }

                      // Create a copy of all current scheduled surveys
                      const newScheduledProcessSurveys = [...scheduledProcessSurveys];

                      // Apply bulk date and onboarding to only the selected employees
                      // Loop through each selected employee and update their settings
                      selectedEmployees.forEach((employee) => {
                        const index = newScheduledProcessSurveys.findIndex((item) => item.employee === employee);
                        if (index >= 0) {
                          // Update existing entry
                          // If no bulk values were set, use the last values from any selected employee
                          const lastUsedDate =
                            bulkDate ||
                            (!bulkDate && selectedEmployees.length > 0
                              ? newScheduledProcessSurveys.find((item) => selectedEmployees.includes(item.employee))
                                  ?.addedOn
                              : undefined);

                          const lastUsedSurveyGroupId =
                            bulkSurveyGroupId ||
                            (!bulkSurveyGroupId && selectedEmployees.length > 0
                              ? newScheduledProcessSurveys.find((item) => selectedEmployees.includes(item.employee))
                                  ?.surveyGroupId
                              : undefined);

                          newScheduledProcessSurveys[index] = {
                            ...newScheduledProcessSurveys[index],
                            // Always apply values, even if they're the same as before
                            // If we have bulk values or last used values, apply them
                            ...(lastUsedDate && { addedOn: lastUsedDate }),
                            ...(lastUsedSurveyGroupId && { surveyGroupId: lastUsedSurveyGroupId }),
                          };
                        } else {
                          // Create new entry
                          const today = new Date();
                          const startOfCurrentMonth = startOfMonth(today);
                          const startOfNextMonth = startOfMonth(addMonths(today, 1));
                          const defaultAddedOn = today.getDate() === 1 ? startOfCurrentMonth : startOfNextMonth;
                          newScheduledProcessSurveys.push({
                            employee,
                            surveyGroupId: bulkSurveyGroupId || defaultSurveyGroupId || '',
                            addedOn: bulkDate || defaultAddedOn,
                          });
                        }
                      });
                      // Set all changes at once
                      setScheduledProcessSurveys(newScheduledProcessSurveys);
                      // Unselect all employees and clear bulk inputs after applying changes
                      setSelectedEmployees([]);
                    }}
                  >
                    {t('acceptButton')}
                  </SubtleButton>
                </div>
              </div>
            </TableRow>
          </BulkEditContainer>
        )}
        <TableHeader>
          <CheckboxCell>
            {isBulkEdit && (
              <Checkbox
                onToggle={() => handleSelectAll(!isAllSelected)}
                selected={isAllSelected}
                partlySelected={isSomeSelected}
                text={'\u00A0'}
                checkIcon={<DeprecatedCheckIcon />}
                lineIcon={<LineIcon />}
                paddingMultiplier={0}
              />
            )}
          </CheckboxCell>
          <div></div>
          <div>{t('addedOn')}</div>
          <div>{processSurveyColumnHeader}</div>
        </TableHeader>

        {employees.map((employee) => {
          const isSelected = selectedEmployees.some((e) => e === employee);
          const scheduledItem = getScheduledItemForEmployee(employee);

          return (
            <TableRow key={employee.id}>
              <CheckboxCell>
                {isBulkEdit && (
                  <Checkbox
                    onToggle={() => handleEmployeeSelection(employee)}
                    selected={isSelected}
                    partlySelected={false}
                    text={'\u00A0'}
                    checkIcon={<DeprecatedCheckIcon />}
                    lineIcon={<LineIcon />}
                    paddingMultiplier={0}
                    dataCy="add-to-onboarding-checkbox"
                  />
                )}
              </CheckboxCell>
              <div>{`${employee.firstname} ${employee.surname}`}</div>
              <div>
                <DateInputWrapper>
                  <DateInput
                    onDateChange={(value) => handleDateChange(employee, value)}
                    inputName={`addedOn-${employee.id}`}
                    date={scheduledItem?.addedOn ? scheduledItem.addedOn.toISOString() : undefined}
                    data-cy={'add-to-onboarding-input'}
                    autoPositioning={true}
                  />
                </DateInputWrapper>
              </div>
              <div>
                <SelectWrapper
                  options={surveyOptions}
                  placeholder={t('select')}
                  isSearchable={false}
                  onChange={(option: { value: string; label: string }) => handleSurveyChange(employee, option.value)}
                  menuPlacement="auto"
                  value={
                    scheduledItem?.surveyGroupId
                      ? surveyOptions.find((option) => option.value === scheduledItem.surveyGroupId)
                      : processSurveys.length === 1
                        ? surveyOptions[0]
                        : undefined
                  }
                  styles={{
                    menu: (provided: any) => ({
                      ...provided,
                      ...selectMenuStyles,
                      top: 'auto',
                      bottom: '100%',
                      marginBottom: `${theme.spacing.d}`,
                      backgroundColor: `${theme.background.primary}`,
                    }),
                    container: (provided: any) => ({
                      ...provided,
                    }),
                    control: (provided: any, state: any) => ({
                      ...provided,
                      paddingLeft: theme.spacing.f,
                      borderColor: state.isFocused
                        ? `${theme.colors.borderMain.active} !important`
                        : theme.colors.borderMain.default,
                      boxShadow: 'none',
                      width: '184px',
                    }),
                  }}
                  isDisabled={surveyOptions.length === 1}
                />
              </div>
            </TableRow>
          );
        })}
      </TableContainer>
    </>
  );
}

const TableRow2 = styled(TableRow)`
  grid-template-columns: 180px;
  grid-auto-columns: 180px;
  grid-auto-flow: column;
`;

const TableHeader2 = styled(TableRow2)`
  color: ${theme.colors.text.secondary};
  font-size: 13px;
  border: none;
`;

const SurveyWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing.g};
`;

const DateInputWrapper = styled.div`
  input {
    width: 200px;
  }
`;

const ValidationError = styled.span`
  color: ${theme.colors.emotionDanger.default};
  font-size: 14px;
  display: block;
  margin-top: ${theme.spacing.h};
  overflow: visible;
  position: absolute;
`;

interface addToOneTimeSurveysModalProps {
  employees: EmployeeRecordWithAdditionalData[];
  surveys: NonNullable<SurveysQuery['user']['organization']>['surveys'];
  scheduledSurveys: ScheduledOneTimeSurvey[];
  setScheduledSurveys: (data: ScheduledOneTimeSurvey[]) => void;
}

export const AddToOneTimeSurveysModal = ({
  employees,
  surveys,
  scheduledSurveys,
  setScheduledSurveys,
}: addToOneTimeSurveysModalProps) => {
  const [t] = useTranslation('organizationImportCard');
  const surveyNamesAndIds = surveys.map((survey) => ({
    name: survey.topicGroup?.name ?? '',
    id: survey.id,
  }));

  const onCheckboxToggle = (employee: EmployeeRecordWithAdditionalData, surveyId: string) => {
    const checked = !isEmployeeScheduledForSurvey(employee, surveyId);
    if (checked) {
      const newScheduledSurveys = [
        ...scheduledSurveys,
        {
          employee,
          surveyId,
        },
      ];
      setScheduledSurveys(newScheduledSurveys);
    } else {
      const newScheduledSurveys = scheduledSurveys.filter(
        (scheduledSurvey) => !(scheduledSurvey.employee === employee && scheduledSurvey.surveyId === surveyId),
      );
      setScheduledSurveys(newScheduledSurveys);
    }
  };
  const isEmployeeScheduledForSurvey = (employee: EmployeeRecordWithAdditionalData, surveyId: string) => {
    return scheduledSurveys.some(
      (scheduledSurvey) => scheduledSurvey.employee === employee && scheduledSurvey.surveyId === surveyId,
    );
  };
  const scheduleSurveyForAll = (surveyId: string) => {
    const checked = !employees.every((employee) => isEmployeeScheduledForSurvey(employee, surveyId));
    if (checked) {
      const newScheduledSurveys = employees
        .map((employee) => ({
          employee,
          surveyId,
        }))
        .filter((scheduledSurvey) => {
          return !scheduledSurveys.some(
            (existingSurvey) =>
              existingSurvey.employee === scheduledSurvey.employee && existingSurvey.surveyId === surveyId,
          );
        });
      setScheduledSurveys([...scheduledSurveys, ...newScheduledSurveys]);
    } else {
      const newScheduledSurveys = scheduledSurveys.filter((s) => s.surveyId !== surveyId);
      setScheduledSurveys(newScheduledSurveys);
    }
  };

  return (
    <>
      <HeadingSmall className="mb-4">{t('addToOneTimeSurveyTitle')}</HeadingSmall>
      <BodyMedium color={theme.colors.text.secondary}>{`${t('addedEmployees')}: ${employees.length}`}</BodyMedium>
      <TableContainer>
        <TableHeader2>
          <div>{t('employee')}</div>
          {surveyNamesAndIds.map((survey) => (
            <SurveyWrapper key={survey.id}>
              <CheckboxCell>
                <Checkbox
                  onToggle={(e) => scheduleSurveyForAll(survey.id)}
                  selected={employees.every((employee) => isEmployeeScheduledForSurvey(employee, survey.id))}
                  partlySelected={
                    employees.some((employee) => isEmployeeScheduledForSurvey(employee, survey.id)) &&
                    !employees.every((employee) => isEmployeeScheduledForSurvey(employee, survey.id))
                  }
                  text={'\u00A0'}
                  checkIcon={<DeprecatedCheckIcon />}
                  lineIcon={<LineIcon />}
                  paddingMultiplier={0}
                />
              </CheckboxCell>
              {survey.name}
            </SurveyWrapper>
          ))}
        </TableHeader2>

        {employees.map((employee) => {
          return (
            <TableRow2 key={employee.id}>
              <div>{`${employee.firstname} ${employee.surname}`}</div>
              {surveyNamesAndIds.map((survey) => (
                <CheckboxCell key={survey.id}>
                  <Checkbox
                    onToggle={() => onCheckboxToggle(employee, survey.id)}
                    selected={isEmployeeScheduledForSurvey(employee, survey.id)}
                    partlySelected={false}
                    text={'\u00A0'}
                    checkIcon={<DeprecatedCheckIcon />}
                    lineIcon={<LineIcon />}
                    paddingMultiplier={0}
                  />
                </CheckboxCell>
              ))}
            </TableRow2>
          );
        })}
      </TableContainer>
    </>
  );
};
